import { PricingEngine } from '../../domain/pricing';
import {
  formatReceiptDate,
  getReceiptAddOnLines,
  getReceiptPayment,
  paymentLabel,
} from '../../domain/receipts/receiptUtils';
import type { ReceiptData } from '../../domain/receipts/receiptTypes';
import type { ReceiptPaperWidth } from '../../services/printing/ReceiptPrinter';
import { AddOnSelectionList } from './AddOnSelectionList';

type Props = { data: ReceiptData; width: ReceiptPaperWidth };

export function ReceiptDocument({ data, width }: Props) {
  const { payment, amountPaidCentavos, changeCentavos } = getReceiptPayment(data);
  const addonLines = getReceiptAddOnLines(data.items);
  return (
    <article className={`receipt receipt--${width === '58mm' ? '58' : '80'}`} aria-label="Receipt">
      <header className="receipt__header">
        <img src="/babs-logo.jpeg" alt="BABS Laundry Shop" className="receipt__logo" />
        <h1>{data.businessName}</h1>
        <p>{data.businessAddress}</p>
        <p>Official Sales Receipt</p>
      </header>

      <section className="receipt__meta">
        <div>
          <span>Order</span>
          <strong>{data.order.orderNumber}</strong>
        </div>
        <div>
          <span>Claim Code</span>
          <strong>{data.order.claimCode ?? '—'}</strong>
        </div>
        <div>
          <span>Date / Time</span>
          <strong>{formatReceiptDate(data.order.createdAt)}</strong>
        </div>
        <div>
          <span>Customer</span>
          <strong>{data.customer?.name ?? 'Walk-in customer'}</strong>
        </div>
        {data.customer?.mobileNumber && (
          <div>
            <span>Mobile</span>
            <strong>{data.customer.mobileNumber}</strong>
          </div>
        )}
      </section>

      <section className="receipt__items">
        <div className="receipt__section-title">SERVICES</div>
        {data.items.map((item) => (
          <div className="receipt__item" key={item.id}>
            <div className="receipt__item-main">
              <span>
                {item.serviceItemName
                  ? `${item.serviceName} — ${item.serviceItemName}`
                  : item.serviceName}
              </span>
              <strong>{PricingEngine.formatCentavos(item.lineTotalCentavos)}</strong>
            </div>
            <div className="receipt__item-detail">
              <span>
                {item.servicePricingType === 'PER_LOAD'
                  ? `${item.quantity} load${item.quantity === 1 ? '' : 's'} • ${item.weightKg ?? '—'} KG`
                  : item.weightKg
                    ? `${item.weightKg} KG${item.quantity > 1 ? ` • Qty ${item.quantity}` : ''}`
                    : `Qty ${item.quantity}`}
              </span>
              {item.serviceItemName && <span>× {item.quantity}</span>}
            </div>
          </div>
        ))}
        {addonLines.length > 0 && (
          <div className="receipt__item mt-2">
            <div className="receipt__section-title">ADD-ONS</div>
            <AddOnSelectionList items={addonLines} label="Receipt add-ons" />
          </div>
        )}
      </section>

      <section className="receipt__totals">
        <div>
          <span>Subtotal</span>
          <strong>{PricingEngine.formatCentavos(data.order.subtotalCentavos)}</strong>
        </div>
        {data.order.discountCentavos > 0 && (
          <div>
            <span>Discount</span>
            <strong>-{PricingEngine.formatCentavos(data.order.discountCentavos)}</strong>
          </div>
        )}
        <div className="receipt__grand-total">
          <span>TOTAL</span>
          <strong>{PricingEngine.formatCentavos(data.order.totalCentavos)}</strong>
        </div>
      </section>

      <section className="receipt__payment">
        <div className="receipt__section-title">PAYMENT</div>
        <div>
          <span>Method</span>
          <strong>{paymentLabel(payment?.method)}</strong>
        </div>
        <div>
          <span>Amount Paid</span>
          <strong>{PricingEngine.formatCentavos(amountPaidCentavos)}</strong>
        </div>
        {payment?.reference && (
          <div>
            <span>Reference</span>
            <strong>{payment.reference}</strong>
          </div>
        )}
        <div>
          <span>Change / Sukli</span>
          <strong>{PricingEngine.formatCentavos(changeCentavos)}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{data.order.paymentStatus}</strong>
        </div>
        <div>
          <span>Order Status</span>
          <strong>{data.order.orderStatus}</strong>
        </div>
      </section>

      <footer className="receipt__footer">
        <p>Thank you for choosing BABS Laundry Shop.</p>
        <p>Please keep this receipt and claim code for order release.</p>
      </footer>
    </article>
  );
}
